import { useEffect, useMemo, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { modulesForPlan, type ModuleKey, type PlanKey } from '../../lib/plans';
import {
  SERVICES,
  FORMULES,
  type BillingMode,
  buildActivation,
  billingStateLabel,
  serviceLabel,
} from '../../lib/serviceActivation';
import {
  adminSetTenantOption,
  loadTenantOptionStates,
  loadAllTenantOptionStates,
  type TenantOptionState,
} from '../../lib/serviceActivationService';
import { loadAllPendingRequests, activateRequest, type OptionRequest } from '../../lib/optionRequestsService';

/**
 * Onglet « Options & services » du SuperAdmin (refonte de « Campagnes réseau »).
 * Idée : activer une OPTION pour un CLIENT donné avec une FORMULE de facturation —
 * Gratuit / Essai gratuit jusqu'à une date (calendrier) / Payant. Sert à donner suite
 * à une demande d'activation client (Edge Function `request-option`), avec possibilité
 * d'offrir une période de gratuité. Persistance : RPC `admin_set_tenant_option` (sql/34).
 */
export default function ServicesSection({ tenants }: { tenants: Tenant[] }) {
  const sorted = useMemo(
    () => [...tenants].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants],
  );

  const [tenantId, setTenantId] = useState('');
  const [service, setService] = useState<ModuleKey>(SERVICES[0]?.key ?? 'loyalty');
  const [mode, setMode] = useState<BillingMode>('free');
  const [trialDate, setTrialDate] = useState('');
  const [states, setStates] = useState<Record<string, TenantOptionState>>({});
  const [loadingStates, setLoadingStates] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Suivi du PARC : état des options de tous les restos d'un coup.
  const [allStates, setAllStates] = useState<Record<string, Record<string, TenantOptionState>>>({});
  const refreshAll = () => loadAllTenantOptionStates().then(setAllStates);
  // Demandes d'options / formules EN ATTENTE (à traiter par le super admin).
  const [requests, setRequests] = useState<OptionRequest[]>([]);
  const [reqBusy, setReqBusy] = useState<Record<string, boolean>>({});
  const refreshRequests = () => loadAllPendingRequests().then(setRequests);
  useEffect(() => {
    refreshAll();
    refreshRequests();
  }, []);

  /** Active une demande : pose les flags (option seule ou toutes celles de la formule) + notifie le client. */
  const activateFromRequest = async (r: OptionRequest) => {
    const t = tenants.find((x) => x.id === r.tenantId);
    const featureKeys =
      r.kind === 'plan' ? (modulesForPlan(r.reqKey as PlanKey) as string[]) : [r.reqKey];
    if (featureKeys.length === 0) {
      setMsg({ kind: 'err', text: `Formule « ${r.reqKey} » sans option à activer.` });
      return;
    }
    setReqBusy((b) => ({ ...b, [r.id]: true }));
    const res = await activateRequest({
      tenantId: r.tenantId,
      tenantEmail: t?.email ?? null,
      featureKeys,
      label: r.label,
      requestId: r.id,
    });
    setReqBusy((b) => ({ ...b, [r.id]: false }));
    if (!res.ok) {
      setMsg({ kind: 'err', text: res.error ?? 'Activation impossible.' });
      return;
    }
    setMsg({ kind: 'ok', text: `✓ « ${r.label} » activé pour ${t?.name ?? 'le resto'} — client notifié.` });
    refreshRequests();
    refreshAll();
  };

  const selectedTenant = sorted.find((t) => t.id === tenantId) ?? null;
  const needsDate = FORMULES.find((f) => f.key === mode)?.needsDate ?? false;
  // Date min du calendrier = demain (l'essai doit être dans le futur).
  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const refreshStates = (id: string) => {
    if (!id) {
      setStates({});
      return;
    }
    setLoadingStates(true);
    loadTenantOptionStates(id)
      .then(setStates)
      .finally(() => setLoadingStates(false));
  };

  useEffect(() => {
    refreshStates(tenantId);
  }, [tenantId]);

  const apply = async (enabled: boolean, forKey?: ModuleKey) => {
    setMsg(null);
    if (!tenantId) {
      setMsg({ kind: 'err', text: 'Choisis d’abord un client.' });
      return;
    }
    const key = forKey ?? service;
    const built = buildActivation({ enabled, mode, trialDate });
    if (!built.ok || !built.payload) {
      setMsg({ kind: 'err', text: built.error ?? 'Paramètres invalides.' });
      return;
    }
    setBusy(true);
    const res = await adminSetTenantOption(tenantId, key, built.payload);
    setBusy(false);
    if (!res.ok) {
      setMsg({
        kind: 'err',
        text: (res.error ?? 'Échec') + ' — vérifie que sql/34_feature_trial.sql est lancé.',
      });
      return;
    }
    setMsg({
      kind: 'ok',
      text: enabled
        ? `✓ ${serviceLabel(key)} activé pour ${selectedTenant?.name} (${FORMULES.find((f) => f.key === mode)?.label}).`
        : `✓ ${serviceLabel(key)} désactivé pour ${selectedTenant?.name}.`,
    });
    refreshStates(tenantId);
    refreshAll();
  };

  const field =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none';

  return (
    <div className="max-w-5xl">
      {/* DEMANDES EN ATTENTE — à traiter en 1 clic (active les flags + notifie le client) */}
      <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-bold text-amber-800">
            📥 Demandes en attente {requests.length > 0 ? `(${requests.length})` : ''}
          </div>
          <button onClick={refreshRequests} className="text-xs font-semibold text-amber-700 underline">
            Rafraîchir
          </button>
        </div>
        {requests.length === 0 ? (
          <p className="text-sm text-amber-700/70">Aucune demande en attente.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {requests.map((r) => {
              const t = tenants.find((x) => x.id === r.tenantId);
              return (
                <li key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2">
                  <div className="min-w-0">
                    <span className="font-semibold">{t?.name ?? r.tenantSlug ?? 'Resto'}</span>
                    <span className="text-slate-400"> · </span>
                    <span>{r.kind === 'plan' ? '📦 ' : '🧩 '}{r.label}</span>
                  </div>
                  <button
                    onClick={() => activateFromRequest(r)}
                    disabled={reqBusy[r.id]}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    style={{ background: '#16A34A' }}
                  >
                    {reqBusy[r.id] ? 'Activation…' : '✓ Activer & notifier'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div
        className="mb-4 rounded-xl p-4 text-white"
        style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' }}
      >
        <div className="text-xs font-bold uppercase opacity-80">🧩 Options &amp; services</div>
        <div className="text-lg font-extrabold">Suivi &amp; activation des options par client</div>
        <div className="text-sm opacity-90">
          Vue d’ensemble du parc ci-dessous. Pour activer/désactiver : choisis le client, l’option et
          la formule (gratuit, essai daté, payant).
        </div>
      </div>

      {/* SUIVI DU PARC — qui a quelle option + formule, en un coup d'œil */}
      <div className="mb-4 overflow-x-auto rounded-xl border border-slate-200 bg-white p-3">
        <div className="mb-2 text-sm font-bold">📊 Suivi des options — tout le parc</div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1">Restaurant</th>
              {SERVICES.map((s) => (
                <th key={s.key} className="px-2 py-1 text-center">
                  {s.emoji} {s.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id} className={'border-t border-slate-100 ' + (tenantId === t.id ? 'bg-indigo-50' : '')}>
                <td className="px-2 py-1.5">
                  <button
                    onClick={() => setTenantId(t.id)}
                    className="font-semibold text-indigo-700 hover:underline"
                    title="Sélectionner pour activer/désactiver"
                  >
                    {t.name}
                  </button>
                </td>
                {SERVICES.map((s) => {
                  const st = allStates[t.id]?.[s.key];
                  const active = st?.enabled === true;
                  return (
                    <td key={s.key} className="px-2 py-1.5 text-center">
                      {active ? (
                        <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                          {billingStateLabel(st?.billingMode, st?.trialEndsAt)}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold opacity-60">Client</span>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={field}>
              <option value="">— Choisir un restaurant —</option>
              {sorted.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (/{t.slug})
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold opacity-60">Option / service</span>
            <select
              value={service}
              onChange={(e) => setService(e.target.value as ModuleKey)}
              className={field}
            >
              {SERVICES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.emoji} {s.title} · {s.priceLabel}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold opacity-60">Formule</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as BillingMode)}
              className={field}
            >
              {FORMULES.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            <span className="text-[11px] opacity-50">
              {FORMULES.find((f) => f.key === mode)?.hint}
            </span>
          </label>

          {needsDate && (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-semibold opacity-60">Fin de la période gratuite</span>
              <input
                type="date"
                value={trialDate}
                min={minDate}
                onChange={(e) => setTrialDate(e.target.value)}
                className={field}
              />
            </label>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => apply(true)}
            disabled={busy || !tenantId}
            className="rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
            style={{ background: '#7C3AED' }}
          >
            {busy ? '…' : 'Activer l’option'}
          </button>
          <button
            onClick={() => apply(false)}
            disabled={busy || !tenantId}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40"
          >
            Désactiver
          </button>
        </div>

        {msg && (
          <div
            className={
              'mt-3 rounded-lg px-3 py-2 text-sm ' +
              (msg.kind === 'ok'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-700')
            }
          >
            {msg.text}
          </div>
        )}
      </div>

      {/* État actuel des options du client sélectionné */}
      {selectedTenant && (
        <div className="mt-5">
          <h2 className="mb-2 text-base font-bold">
            Options de {selectedTenant.name}
          </h2>
          {loadingStates ? (
            <p className="opacity-60">Chargement…</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase opacity-60">
                  <tr>
                    <th className="px-3 py-2">Option</th>
                    <th className="px-3 py-2">État</th>
                    <th className="px-3 py-2">Formule</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {SERVICES.map((s) => {
                    const st = states[s.key];
                    const active = st?.enabled === true;
                    return (
                      <tr key={s.key} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-medium">
                          {s.emoji} {s.title}
                        </td>
                        <td className="px-3 py-2">
                          {active ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                              Activée
                            </span>
                          ) : (
                            <span className="text-slate-400">Inactive</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {active ? billingStateLabel(st?.billingMode, st?.trialEndsAt) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {active && (
                            <button
                              onClick={() => apply(false, s.key)}
                              disabled={busy}
                              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
                            >
                              Désactiver
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
